import { useEffect, useState } from "react";
import { apiFetch, requireOk } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

export function TenantOnboardingWizard({ onDone }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [studio, setStudio] = useState({
    company_name: "",
    abn: "",
    invoice_prefix: "INV",
    timezone: "Australia/Sydney",
  });
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("employee");

  const saveStudio = async () => {
    if (!studio.company_name.trim()) {
      toast.error("Studio name is required");
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch("/settings/studio", {
        method: "PUT",
        body: JSON.stringify(studio),
      });
      await requireOk(res, "Could not save studio settings");
      setStep(2);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) {
      setStep(3);
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch("/admin/invites", {
        method: "POST",
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      await requireOk(res, "Could not send invite");
      toast.success("Invite sent");
      setStep(3);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      const res = await apiFetch("/tenant/onboarding/complete", { method: "POST" });
      await requireOk(res, "Could not complete onboarding");
      onDone?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="tenant-onboarding-wizard">
      <div className="bg-brand-panel rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4">
        <h2 className="text-xl font-semibold">Set up your studio</h2>
        <p className="text-xs text-brand-steel">Step {step} of 3</p>
        {step === 1 && (
          <div className="space-y-3">
            <Input placeholder="Studio name" value={studio.company_name} onChange={(e) => setStudio({ ...studio, company_name: e.target.value })} />
            <Input placeholder="ABN (11 digits)" value={studio.abn} onChange={(e) => setStudio({ ...studio, abn: e.target.value })} />
            <Input placeholder="Invoice prefix" value={studio.invoice_prefix} onChange={(e) => setStudio({ ...studio, invoice_prefix: e.target.value })} />
            <Input placeholder="Timezone" value={studio.timezone} onChange={(e) => setStudio({ ...studio, timezone: e.target.value })} />
            <Button disabled={busy} onClick={saveStudio}>Continue</Button>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm">Invite your first teammate (optional).</p>
            <Input type="email" placeholder="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
            <select className="border rounded-md px-2 py-1 text-sm bg-transparent" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
              <option value="executive">Executive</option>
            </select>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(3)}>Skip</Button>
              <Button disabled={busy} onClick={sendInvite}>Continue</Button>
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-3">
            <p className="text-sm">You are ready. Open the dashboard when you want to start tracking time.</p>
            <Button disabled={busy} onClick={finish} data-testid="tenant-onboarding-finish">Go to dashboard</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function TenantOnboardingGate() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch("/tenant/onboarding");
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data && data.completed === false) setShow(true);
      } catch (err) {
        if (err && err.message) {
          console.warn("tenant onboarding status unavailable", err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!show) return null;
  return <TenantOnboardingWizard onDone={() => setShow(false)} />;
}

export default TenantOnboardingWizard;
